
import { Component, Vue } from 'vue-property-decorator';
import ScopesRepository from '@/services/Scopes';
import { Treeview } from '@/views/interfaces/vuetify/treeview.interface';
import { ScopeTree } from '@/services/Scopes/Models/ScopeTree';
import RoleRepository from '@/services/Roles';
import {
  PermissionTypes,
  RoleInterface,
} from '@/services/Roles/Interfaces';
import { ScopeInterface } from '@/services/Scopes/Interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import CreateRoleForm from '@/services/Auth/forms/CreateRoleForm';
import { DepartmentInterface } from '@/services/Departments/Interfaces';
import DepartmentRepository from '@/services/Departments';
import PageHeader from '@/components/layout/PageHeader.vue';

@Component({
  inject: [
    'scopesRepository',
    'rolesRepository',
    'departmentRepository',
  ],
  components: {
    PageHeader,
  },
})
export default class EditRole extends Vue {
  private readonly rolesRepository!: RoleRepository;
  private readonly scopesRepository!: ScopesRepository;
  private readonly departmentRepository!: DepartmentRepository;
  private extendedRoles: Array<Partial<RoleInterface>> = [];
  private role: RoleInterface = {
    id: '',
    name: '',
    scopes: [],
    permissions: [],
    department: { _id: '', name: '' },
  };

  private loading: boolean = true;

  private form: WPXFormValue<CreateRoleForm> = new WPXFormValue(CreateRoleForm);

  private treeKey: number = 0;
  private openedBranches: string[] = [];

  private roles: RoleInterface[] = [];
  private departments: DepartmentInterface[] = [];

  private scopesLoading: boolean = false;
  private scopeTreeMap: ScopeTree[] = [];

  get scopes(): Treeview[] {
    return this.scopeTreeMap.map((tree: ScopeTree) => {
      return tree.toTreeView();
    });
  }

  private created(): void {
    Promise.all([
      this.initializeDepartments(),
      this.initializeScopes(),
      this.initializeRoles(),
    ]).then(() => {
      this.loading = false;
    });
  }

  private async initializeDepartments(): Promise<void> {
    try {
      const departments = await this.departmentRepository.getAllDepartments();

      departments.map((department: DepartmentInterface) => {
        this.departments.push(department);
      });
    } catch (err) {
      this.scopesLoading = false;
      this.$errorDialog.show('Could not retrieve departments.');
    }
  }

  private async initializeRole(): Promise<void> {
    try {
      this.role = await this.rolesRepository.getRoleById(this.$route.params.roleId);
      this.form.data.name = this.role.name;
      this.form.data.selectedScopes = this.role.scopes.map((scope: ScopeInterface) => scope.id);
      this.form.data.department = this.role.department._id;
      this.extendedRoles = this.role.permissions
        .filter((permission) => permission.type === PermissionTypes.ROLE)
        .map((permission) => {
          return { id: permission.role._id, name: permission.role.name, scopes: [], permissions: [] };
        });
      this.extendedRoles.forEach((role) => this.addExtendedRole(String(role.id)));
      this.roles = this.roles.filter((role) => {
        return this.role.id !== role.id && role.name !== 'admin';
      });
    } catch (err) {
      this.loading = false;
      this.$errorDialog.show('Could not retrieve role.');
    }
  }

  private async initializeScopes(): Promise<void> {
    try {
      const scopes = await this.scopesRepository.getAllScopes();
      this.scopeTreeMap = this.scopesRepository.generateScopeTrees(scopes);
    } catch (err) {
      this.scopesLoading = false;
      this.$errorDialog.show('Could not retrieve scopes.');
    }
  }

  private async initializeRoles(): Promise<void> {
    try {
      const roles = await this.rolesRepository.getAllRoles();
      roles.map((role: RoleInterface) => {
        this.roles.push({
          id: role.id,
          name: role.name,
          scopes: role.scopes,
          permissions: [],
          department: role.department,
        });
      });
      await this.initializeRole();
    } catch (err) {
      this.loading = false;
      this.$errorDialog.show('Could not retrieve roles.');
    }
  }

  private async setRoleExtension(change: string[]): Promise<void> {
    const extendedRoleIds = this.form.data.extendedRoles.map((role: RoleInterface) => role.id);
    change.forEach((roleId: string) => {
      if (extendedRoleIds.indexOf(roleId) === -1) {
        this.addExtendedRole(roleId);
      }
    });

    extendedRoleIds.forEach((roleId: string) => {
      if (change.indexOf(roleId) === -1) {
        this.removeExtendedRole(roleId);
      }
    });
  }

  private addExtendedRole(roleId: string): void {
    const newRole = this.roles.find((role: RoleInterface) => role.id === roleId);

    if (newRole === undefined) {
      this.$errorDialog.show(`Could not find role with id: ${roleId}`);
      return;
    }

    this.form.data.extendedRoles.push(newRole);
    this.updateRoleScopesStatus(newRole, true);
  }

  private removeExtendedRole(roleId: string): void {
    const oldRole = this.form.data.extendedRoles.find((role: RoleInterface) => role.id === roleId);

    if (oldRole === undefined) {
      this.$errorDialog.show(`Could not find role with id: ${roleId}`);
      return;
    }

    this.form.data.extendedRoles.splice(this.form.data.extendedRoles.indexOf(oldRole), 1);
    this.updateRoleScopesStatus(oldRole, false);
  }

  private updateRoleScopesStatus(role: RoleInterface, disabled: boolean, copy: boolean = false): void {

    role.scopes?.forEach((scope: ScopeInterface) => {
      const [service, func] = scope.name.split(':');
      const scopeTree = this.scopeTreeMap.find((tree: ScopeTree) => tree.name === service);

      if (scopeTree === undefined) {
        return;
      }

      this.openedBranches.push(scopeTree.id);

      const treeLeaf = scopeTree.getChild(func).getChild(scope.name);
      treeLeaf.disabled = disabled;
      if (disabled || copy) {
        this.form.data.selectedScopes.push(treeLeaf.id);
      } else {
        this.form.data.selectedScopes.splice(this.form.data.selectedScopes.indexOf(treeLeaf.id), 1);
        this.form.data.selectedScopes = JSON.parse(JSON.stringify(this.form.data.selectedScopes));
      }
    });
    this.treeKey++;
  }

  private async editRole(): Promise<void> {
    let extRoleScopes: string[] = [];
    this.form.data.extendedRoles.forEach((role: RoleInterface) => {
      if (!role.scopes) {
        return;
      }
      extRoleScopes = [...extRoleScopes, ...role.scopes.map((scope: ScopeInterface) => scope.id)];
    });

    const newScopes: string[] = [];
    this.form.data.selectedScopes.forEach((scope: string) => {
      if (extRoleScopes.indexOf(scope) === -1) {
        newScopes.push(scope);
      }
    });

    try {
      await this.rolesRepository.updateRole({
        id: this.role.id,
        name: this.form.data.name,
        scopes: newScopes,
        extensions: this.form.data.extendedRoles.map((role: RoleInterface) => role.id),
        department: this.form.data.department,
      });

      this.$successDialog.show(`Successfully updated role '${this.form.data.name}'`);
    } catch (err) {
      this.$errorDialog.show(err.response.data.message);
    }
  }

  private async syncScopes(): Promise<void> {
    this.scopesLoading = true;
    await this.scopesRepository.syncScopes();
    await this.initializeScopes();
    this.scopesLoading = false;
  }
}
